import { ArrowRight, BookOpen, Globe2, Image, LockKeyhole, Plus, Search, Sparkles, UploadCloud } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { createCourse, getCourses } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { generateCourseCode } from '../../utils/courseCode';
import { ROOT_DOMAIN } from '../../utils/hostname';
import { StatusBadge } from '../../components/common/StatusBadge';
import { COURSE_TYPES, getCourseTypeLabel } from '../../utils/courseTypes';
import { DURATION_UNITS, PAYMENT_TERMS, formatCourseDuration, recommendedPaymentTerm } from '../../utils/courseBilling';
import defaultCourseImage from '../../assets/newimage29-optimized.webp';
import { storage } from '../../firebase/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

export function CoursesPage() {
  const { user } = useAuth();
  const { activeTenant, activeTenantId } = useTenant();
  const { notify } = useToast();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourseType, setFilterCourseType] = useState('All');
  const [values, setValues] = useState({ name: '', code: '', description: '', durationValue: '', durationUnit: 'weeks', feeAmount: '', courseType: 'live_online', paymentTerm: 'weekly', imageURL: defaultCourseImage });
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const nextCourses = await getCourses(activeTenantId);
    setCourses(nextCourses.filter((course) => course.status !== 'deleted'));
    setLoading(false);
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  async function onSubmit(event) {
    event.preventDefault();
    const courseCode = values.code.trim() || generateCourseCode(values.name);
    const isPrivateInstructor = activeTenant?.type === 'private_instructor';
    const instructorId = isPrivateInstructor ? user.uid : null;
    const instructorName = isPrivateInstructor ? (user.displayName || user.email || '') : '';
    setSaving(true);
    try {
      let imageURL = values.imageURL || defaultCourseImage;
      if (imageFile) {
        setUploadingImage(true);
        const safeName = imageFile.name.replace(/[^a-zA-Z0-9._-]/g, '-');
        const imageRef = ref(storage, `tenants/${activeTenantId}/courses/${Date.now()}-${safeName}`);
        const snapshot = await uploadBytes(imageRef, imageFile, { contentType: imageFile.type });
        imageURL = await getDownloadURL(snapshot.ref);
      }
      await createCourse(activeTenantId, user.uid, { ...values, imageURL, paymentTerm: values.courseType === 'self_paced' ? 'full' : values.paymentTerm, duration: formatCourseDuration(values.durationValue, values.durationUnit), code: courseCode, instructorId, instructorName });
      notify('Course created successfully.');
      setValues({ name: '', code: '', description: '', durationValue: '', durationUnit: 'weeks', feeAmount: '', courseType: 'live_online', paymentTerm: 'weekly', imageURL: defaultCourseImage });
      setImageFile(null);
      setOpen(false);
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to save course. Try again.', 'error');
    } finally {
      setUploadingImage(false);
      setSaving(false);
    }
  }

  const currency = activeTenant?.settings?.currency || 'KES';
  const academySubdomain = activeTenant?.slug
    ? `${activeTenant.slug}.${ROOT_DOMAIN}`
    : `youracademy.${ROOT_DOMAIN}`;
  const academySubdomainURL = useMemo(() => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost');
    const host = isLocal ? `${activeTenant?.slug || 'youracademy'}.localhost` : academySubdomain;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//${host}${port}`;
  }, [academySubdomain, activeTenant?.slug]);
  const publishedCourses = courses.filter((course) => course.publicEnrollment?.enabled).length;
  const privateCourses = courses.length - publishedCourses;
  const catalogueValue = courses.reduce((sum, course) => sum + Number(course.feeAmount || 0), 0);
  const visibleCourses = courses.filter((course) => {
    const query = searchTerm.trim().toLowerCase();
    const matchesType = filterCourseType === 'All' || course.courseType === filterCourseType;
    if (!matchesType) return false;
    if (!query) return true;
    return [course.name, course.code, course.description, course.duration]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  return (
    <div className="page-stack coursesPage">
      <section className="courses-intro">
        <div className="courses-intro-copy">
          <span className="courses-kicker">Learning library</span>
          <h1>Build a catalogue<br /><i>people want to join.</i></h1>
          <p>Create, publish and grow every learning offer from one calm workspace. Your courses become the foundation for admissions, classes, payments and progress.</p>
        </div>
        <div className="courses-intro-actions">
          <a className="btn btn-secondary" href={academySubdomainURL} target="_blank" rel="noreferrer">View public page</a>
          <Button icon={Plus} onClick={() => setOpen(true)}>New course</Button>
        </div>
      </section>

      <section className="courses-snapshot" aria-label="Course catalogue overview">
        <article className="courses-snapshot-card courses-snapshot-card--primary">
          <span className="courses-snapshot-icon"><BookOpen size={16} /></span>
          <div><small>Total courses</small><strong>{courses.length}</strong><p>Your full learning catalogue.</p></div>
        </article>
        <article className="courses-snapshot-card courses-snapshot-card--green">
          <span className="courses-snapshot-icon"><Globe2 size={16} /></span>
          <div><small>Published</small><strong>{publishedCourses}</strong><p>Visible to prospective learners.</p></div>
        </article>
        <article className="courses-snapshot-card courses-snapshot-card--blue">
          <span className="courses-snapshot-icon"><LockKeyhole size={16} /></span>
          <div><small>Private offers</small><strong>{privateCourses}</strong><p>Still being prepared or invite-only.</p></div>
        </article>
        <article className="courses-snapshot-card courses-snapshot-card--warm">
          <span className="courses-snapshot-icon"><Sparkles size={16} /></span>
          <div><small>Catalogue value</small><strong>{formatCurrency(catalogueValue, currency)}</strong><p>Combined listed course fees.</p></div>
        </article>
      </section>

      <div className="courses-toolbar">
          <label className="courses-search" htmlFor="courses-search">
            <Search size={16} aria-hidden="true" />
            <input id="courses-search" type="search" placeholder="Search your catalogue..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
          </label>
        <select className="courses-type-filter" aria-label="Filter courses by type" value={filterCourseType} onChange={(event) => setFilterCourseType(event.target.value)}>
          <option value="All">All course types</option>
          {COURSE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
        </select>
        <span className="courses-toolbar-count">Showing {visibleCourses.length} of {courses.length}</span>
      </div>

      {loading ? <LoadingState label="Loading courses..." /> : (
        visibleCourses.length ? (
          <div className="course-card-grid">
            {visibleCourses.map((course, index) => (
              <Link className="course-card-link" to={`/dashboard/courses/${course.id}`} key={course.id}>
                <article className="course-card">
                  <div className="course-card-image"><img src={course.imageURL || defaultCourseImage} alt="" /></div>
                  <div className="course-card-top">
                    <div className="course-card-index">{String(index + 1).padStart(2, '0')}</div>
                    <div className="empty-icon"><BookOpen size={20} /></div>
                    <StatusBadge status={course.status} />
                  </div>
                  <h3>{course.name}</h3>
                  <p>{course.description || 'No description added yet.'}</p>
                  <div className="course-meta-row">
                    <strong>{course.code || 'CODE unset'}</strong>
                    <strong>{course.duration || 'Duration unset'}</strong>
                  </div>
                  <span className="course-type-label">{getCourseTypeLabel(course.courseType)}</span>
                  <span className="course-type-label">{PAYMENT_TERMS.find((term) => term.value === course.paymentTerm)?.label || 'Payment term not set'}</span>
                  <div className="course-card-footer">
                    <span className={course.publicEnrollment?.enabled ? 'is-published' : 'is-private'}>{course.publicEnrollment?.enabled ? <><Globe2 size={13} /> Published</> : <><LockKeyhole size={13} /> Private</>}</span>
                    <b>{formatCurrency(course.feeAmount || 0, currency)}</b>
                    <span className="course-open-link">Open course <ArrowRight size={16} /></span>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState title="You have not created any courses yet." message="Create your first course to begin building your academy." actionLabel="Create Course" onAction={() => setOpen(true)} icon={BookOpen} />
        )
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Course" description="Add the key details now. You can build out classes, lessons and enrolment later." className="course-create-modal">
        <form onSubmit={onSubmit} className="modal-form course-create-form">
          <div className="course-modal-intro"><span className="course-modal-intro-icon"><BookOpen size={18} /></span><div><strong>Shape your learning offer</strong><p>Set the essentials now. You can add classes, curriculum and learning materials after saving.</p></div></div>
          {activeTenant?.type === 'private_instructor' ? (
            <div className="notice-box">You are the instructor for this private academy course. Your name will be saved automatically.</div>
          ) : null}
          <FormField label="Course Name" id="course-name"><input id="course-name" required value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} /></FormField>
          <FormField label="Course Code" id="course-code"><input id="course-code" placeholder={generateCourseCode(values.name)} value={values.code} onChange={(event) => setValues({ ...values, code: event.target.value.toUpperCase() })} /></FormField>
          <div className="form-grid">
            <FormField label="Course duration" id="course-duration-value"><input id="course-duration-value" type="number" min="1" step="1" required placeholder="12" value={values.durationValue} onChange={(event) => { const durationValue = event.target.value; setValues({ ...values, durationValue, paymentTerm: recommendedPaymentTerm(durationValue, values.durationUnit) }); }} /></FormField>
            <FormField label="Duration unit" id="course-duration-unit"><select id="course-duration-unit" required value={values.durationUnit} onChange={(event) => { const durationUnit = event.target.value; setValues({ ...values, durationUnit, paymentTerm: recommendedPaymentTerm(values.durationValue, durationUnit) }); }}>{DURATION_UNITS.map((unit) => <option key={unit.value} value={unit.value}>{unit.label}</option>)}</select></FormField>
            <FormField label={`Price / Fee (${currency})`} id="course-fee"><input id="course-fee" type="number" min="0" step="1" required value={values.feeAmount} onChange={(event) => setValues({ ...values, feeAmount: event.target.value })} /></FormField>
          </div>
          <FormField label="Course type" id="course-type"><select id="course-type" required value={values.courseType} onChange={(event) => { const courseType = event.target.value; setValues({ ...values, courseType, paymentTerm: courseType === 'self_paced' ? 'full' : values.paymentTerm }); }}>{COURSE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select><small>{COURSE_TYPES.find((type) => type.value === values.courseType)?.description}</small></FormField>
          <FormField label="Fee payment term" id="course-payment-term"><select id="course-payment-term" required disabled={values.courseType === 'self_paced'} value={values.courseType === 'self_paced' ? 'full' : values.paymentTerm} onChange={(event) => setValues({ ...values, paymentTerm: event.target.value })}>{PAYMENT_TERMS.filter((term) => values.courseType !== 'self_paced' || term.value === 'full').map((term) => <option key={term.value} value={term.value}>{term.label}</option>)}</select><small>{values.courseType === 'self_paced' ? 'Self-paced digital courses are paid in one full installment.' : `Recommended: ${PAYMENT_TERMS.find((term) => term.value === recommendedPaymentTerm(values.durationValue, values.durationUnit))?.label}`}</small></FormField>
          <label className="course-image-upload" htmlFor="course-image"><span><UploadCloud size={16} /> Course image</span><small>{imageFile ? imageFile.name : 'Use the default image or upload a replacement.'}</small><input id="course-image" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { const file = event.target.files?.[0] || null; setImageFile(file); if (file) setValues({ ...values, imageURL: URL.createObjectURL(file) }); }} /></label>
          <div className="course-image-preview"><img src={values.imageURL || defaultCourseImage} alt="Course preview" /><span><Image size={14} /> {uploadingImage ? 'Uploading image...' : 'This image appears on your dashboard and public course card.'}</span></div>
          <FormField label="Description" id="course-description"><textarea id="course-description" value={values.description} onChange={(event) => setValues({ ...values, description: event.target.value })} /></FormField>
          <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" loading={saving}>Save Course</Button></div>
        </form>
      </Modal>
    </div>
  );
}
